import { useCallback, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import env from "../../../config/env";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import DeleteTaskModal from "../../Tasks/components/PromptModal/components/DeleteTaskModal";
import ShareChatModal from "../components/ShareChatModal";

const useTaskCardActions = ({ onTasksChanged } = {}) => {
  const BASE_URL = `${env.API_URL}/v1`;
  const {
    fetchTasksData,
    addTasksFavourite,
    removeTasksFav,
    selectedAssistant,
  } = useUsersContext();
  const [, , , , userActiveLocation] = useUserInfo();
  const navigate = useNavigate();

  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskToShare, setTaskToShare] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fallbackAssistant = selectedAssistant || {
    assistant_name: "Brand AI",
    modal_name: "openai/gpt-4o",
    assistant_id: userActiveLocation?.assistant_id,
    status: true,
  };

  const refetch = useCallback(async () => {
    await fetchTasksData?.();
    await onTasksChanged?.();
  }, [fetchTasksData, onTasksChanged]);

  const handleEdit = useCallback(
    (e, task) => {
      e?.stopPropagation?.();
      const taskId = task?._id || task?.id;
      if (!taskId) return;
      navigate(`/app/chat/tasks/${taskId}/edit`);
    },
    [navigate],
  );

  const handleDeleteOpen = useCallback((e, task) => {
    e?.stopPropagation?.();
    setTaskToDelete(task);
  }, []);

  const handleDeleteClose = useCallback(() => {
    if (deleteLoading) return;
    setTaskToDelete(null);
  }, [deleteLoading]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!taskToDelete?._id) return;
    setDeleteLoading(true);
    try {
      const endpoint =
        taskToDelete?.repeat && taskToDelete.repeat.toLowerCase() !== "none"
          ? "/prompts/delete/scheduled"
          : "/chat/promptDelete";
      await axios.delete(`${BASE_URL}${endpoint}?_id=${taskToDelete._id}`);
      toast.success("Task deleted successfully.");
      setTaskToDelete(null);
      await refetch();
    } catch (error) {
      toast.error("Something went wrong while deleting the task.");
    } finally {
      setDeleteLoading(false);
    }
  }, [taskToDelete, BASE_URL, refetch]);

  const handleShareOpen = useCallback((e, task) => {
    e?.stopPropagation?.();
    setTaskToShare(task);
  }, []);

  const handleShareClose = useCallback(() => setTaskToShare(null), []);

  const handlePin = useCallback(
    (e, task) => {
      e?.stopPropagation?.();
      if (!task?._id) return;
      if (task?.isFavourite) {
        removeTasksFav(e, task._id);
      } else {
        addTasksFavourite(e, task._id);
      }
    },
    [addTasksFavourite, removeTasksFav],
  );

  const bindActions = useCallback(
    (task) => ({
      onDelete: (e) => handleDeleteOpen(e, task),
      onShare: (e) => handleShareOpen(e, task),
      onEdit: (e) => handleEdit(e, task),
      onPin: (e) => handlePin(e, task),
    }),
    [handleDeleteOpen, handleShareOpen, handleEdit, handlePin],
  );

  const actionModals = (
    <>
      {taskToDelete && (
        <DeleteTaskModal
          isOpen={Boolean(taskToDelete)}
          isScheduledTask={
            taskToDelete?.repeat &&
            taskToDelete.repeat.toLowerCase() !== "none"
          }
          loading={deleteLoading}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <ShareChatModal
        isOpen={Boolean(taskToShare)}
        onClose={handleShareClose}
        contact={taskToShare}
        shareType="task"
        resourceTitle={taskToShare?.title}
        onSuccess={refetch}
      />
    </>
  );

  return {
    bindActions,
    actionModals,
    openCreateTask: () => {
      navigate("/app/chat/tasks/new");
    },
    fallbackAssistant,
  };
};

export default useTaskCardActions;
